import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, Sparkles, BookOpen, Heart, Palette, X } from 'lucide-react';

const STORAGE_KEY = 'lumera_onboarding_completed';

const SLIDES = [
  {
    id: 'welcome',
    title: 'Welcome to Lumera',
    subtitle: 'Your digital product creator',
    body: 'Lumera helps you create beautiful, personalized digital products — from wedding planners to baby books, travel guides and more.',
    Icon: Sparkles,
    accent: '#B49A88',
    bgGradient: 'from-[#F7F5F2] to-[#EDE8E2]',
  },
  {
    id: 'generator',
    title: '33+ Templates',
    subtitle: 'AI-powered content generation',
    body: 'Choose from over 33 unique templates. Our AI generates content, images and layouts — or customize yourself with our visual editor.',
    Icon: BookOpen,
    accent: '#8B7355',
    bgGradient: 'from-[#F5F0E8] to-[#EDE6DA]',
  },
  {
    id: 'wedding',
    title: 'Wedding Planner',
    subtitle: 'Everything in one place',
    body: 'Guests, seating, flowers, moodboard, budget, music and more — in a beautiful, connected planner that keeps track for you.',
    Icon: Heart,
    accent: '#B76E79',
    bgGradient: 'from-[#F8E8E0] to-[#F0DDD5]',
  },
  {
    id: 'marketplace',
    title: 'Templates & Collections',
    subtitle: 'Premium design, ready to use',
    body: 'Explore our library of professional templates, save favorites, and organize your products into collections you can share.',
    Icon: Palette,
    accent: '#7A8B5C',
    bgGradient: 'from-[#F0F5EB] to-[#E5EEDc]',
  },
];

// Check if onboarding should show
// Uses localStorage now, but structured so backend flag can be added later:
// if (user.onboarding_completed) return false;
export const shouldShowOnboarding = (user) => {
  if (!user) return false;
  // Future: check user.onboarding_completed from DB
  // if (user.onboarding_completed) return false;
  const completed = localStorage.getItem(STORAGE_KEY);
  if (completed) return false;
  return true;
};

export const markOnboardingComplete = (/* userId */) => {
  localStorage.setItem(STORAGE_KEY, 'true');
  // Future: PATCH /api/users/:userId { onboarding_completed: true }
};

export const Onboarding = ({ user, onComplete }) => {
  const [step, setStep] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [direction, setDirection] = useState('next');
  const slide = SLIDES[step];
  const isLast = step === SLIDES.length - 1;

  const goNext = useCallback(() => {
    if (animating) return;
    if (isLast) {
      markOnboardingComplete(user?.id);
      onComplete();
      return;
    }
    setDirection('next');
    setAnimating(true);
    setTimeout(() => { setStep(s => s + 1); setAnimating(false); }, 300);
  }, [step, animating, isLast, onComplete, user]);

  const skip = () => {
    markOnboardingComplete(user?.id);
    onComplete();
  };

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') goNext();
      if (e.key === 'Escape') skip();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [goNext]);

  return (
    <div data-testid="onboarding-overlay" className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-lg mx-4">
        {/* Card */}
        <div className={`relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-b ${slide.bgGradient} transition-all duration-300`}
          data-testid="onboarding-card">
          {/* Skip */}
          <button onClick={skip} data-testid="onboarding-skip"
            className="absolute top-4 right-4 p-1.5 rounded-full text-stone-400 hover:text-stone-600 hover:bg-white/50 transition-colors z-10">
            <X className="w-4 h-4" />
          </button>

          {/* Content */}
          <div className={`p-10 pt-12 text-center transition-all duration-300 ${animating ? 'opacity-0 translate-x-8' : 'opacity-100 translate-x-0'}`}>
            {/* Icon */}
            <div className="w-16 h-16 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-sm"
              style={{ background: `${slide.accent}15`, border: `2px solid ${slide.accent}30` }}>
              <slide.Icon className="w-7 h-7" style={{ color: slide.accent }} />
            </div>

            {/* Text */}
            <p className="text-[10px] font-sans font-bold uppercase tracking-[0.2em] mb-2" style={{ color: slide.accent }}>{slide.subtitle}</p>
            <h2 className="text-2xl font-serif font-bold text-stone-800 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{slide.title}</h2>
            <p className="text-sm font-sans text-stone-600 leading-relaxed max-w-sm mx-auto">{slide.body}</p>
          </div>

          {/* Footer */}
          <div className="px-10 pb-8 flex items-center justify-between">
            {/* Dots */}
            <div className="flex items-center gap-2">
              {SLIDES.map((_, i) => (
                <div key={i}
                  className={`rounded-full transition-all duration-300 ${i === step ? 'w-6 h-2' : 'w-2 h-2'}`}
                  style={{ background: i === step ? slide.accent : `${slide.accent}30` }} />
              ))}
            </div>

            {/* Next button */}
            <button onClick={goNext} data-testid="onboarding-next"
              className="flex items-center gap-2 text-sm font-sans font-semibold text-white px-5 py-2.5 rounded-full transition-all hover:scale-105 active:scale-95 shadow-md"
              style={{ background: slide.accent }}>
              {isLast ? 'Get started' : 'Next'}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
